import { HttpErrorResponse } from '@angular/common/http';
import {
  DestroyRef,
  Injectable,
  PLATFORM_ID,
  type Signal,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, ReplaySubject, Subscription, catchError, filter, finalize, map, of } from 'rxjs';

import { AuthService } from '../../../shared/services/auth.service';
import { retryAfterSeconds } from '../../../shared/services/newsletter.service';
import { PROFILE_COPY } from '../profile.copy';

/**
 * Los dos botones de correo de la cuenta: `resend` = «Reenviar correo de verificación» (cabecera y ajustes, la misma
 * espera); `reset` = «Enviar enlace» de la contraseña.
 */
export type AccountMailChannel = 'resend' | 'reset';

/** Resultado de un envío, para el aviso que pinta quien lo pidió. */
export type AccountMailOutcome =
  | { kind: 'sent' }
  | { kind: 'rate-limited' }
  | { kind: 'rejected'; message: string }
  | { kind: 'error' };

/** Aviso que se pinta bajo un botón de correo. */
export interface AccountMailNotice {
  tone: 'success' | 'error';
  text: string;
}

/** Texto de §7.2 para cada resultado; `sentText` es el éxito del botón («Te enviamos el correo…», «Listo…»). */
export function accountMailNotice(outcome: AccountMailOutcome, sentText: string): AccountMailNotice {
  switch (outcome.kind) {
    case 'sent':
      return { tone: 'success', text: sentText };
    case 'rate-limited':
      return { tone: 'error', text: PROFILE_COPY.SETTINGS.MAIL_RATE_LIMITED };
    case 'rejected':
      return { tone: 'error', text: outcome.message };
    case 'error':
      return { tone: 'error', text: PROFILE_COPY.SETTINGS.MAIL_ERROR };
  }
}

/** Espera tras cada envío que llega a la API (B24/B43, §5.6.5): 60 s. */
export const ACCOUNT_MAIL_WAIT_S = 60;

const CHANNELS: readonly AccountMailChannel[] = ['resend', 'reset'];

type PerChannel<T> = Record<AccountMailChannel, T>;

/** Toda la espera es de UNA cuenta (`owner`): con otra cuenta (o sin sesión) no vale nada. */
interface CooldownState {
  owner: number | null;
  /** Fin absoluto de la espera (ms epoch), como `CountdownComponent`: nunca un contador decrementado. */
  until: PerChannel<number>;
  /** Envío en vuelo: el botón no deja pedir otro hasta que vuelva. */
  busy: PerChannel<boolean>;
}

const EMPTY_STATE: CooldownState = {
  owner: null,
  until: { resend: 0, reset: 0 },
  busy: { resend: false, reset: false },
};

/**
 * Espera compartida de los botones de correo de la cuenta (W3 de B10, decisión de Adrián del 02/10/2026). La cabecera
 * del perfil y la pestaña de ajustes pintan el mismo `<app-mail-cooldown-button channel="resend">`: pulsar uno deja
 * en espera al otro, porque la espera vive aquí y no en cada botón.
 *
 * - **Tras cada envío con 200 o 429**: 60 s; en un 429, `max(60, Retry-After)`. Un 422 (ya verificada, el correo ya
 *   no es el de la cuenta) o un 5xx/red no arrancan espera: no salió correo.
 * - El limitador `account-mail` de la API (B43/B47) da **3 envíos por cuenta en 10 min** compartidos entre
 *   reenviar, `restore-password` y el reenvío por correo, y `/restore-password` resuelve la cuenta por el correo: así
 *   que «Enviar enlace» y «Reenviar» **comparten cupo** en la API aunque aquí sean canales distintos, y el cupo se
 *   gasta también con los 422 (el throttle corre antes que el controlador). El 4.º da 429 con `Retry-After`.
 * - **Atada a la cuenta**: todo lleva el id de quien la arrancó. Con otra cuenta (o sin sesión) `remaining()` da 0 en
 *   el acto, sin esperar a ningún efecto; el efecto además vacía el estado, y una respuesta que llega con otra
 *   cuenta dentro no arranca espera ni emite.
 * - El reloj (`setInterval` de 1 s) solo corre en navegador y mientras quede alguna espera; se para solo al acabar,
 *   al cambiar de cuenta y al destruirse el inyector.
 */
@Injectable({ providedIn: 'root' })
export class AccountMailCooldownStore {
  private readonly currentUser = inject(AuthService).currentUser;
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly state = signal<CooldownState>(EMPTY_STATE);
  private readonly now = signal(Date.now());
  private timer: ReturnType<typeof setInterval> | null = null;
  /** Envío en vuelo por canal, de la cuenta actual. */
  private readonly requests: PerChannel<Subscription | null> = { resend: null, reset: null };

  private readonly currentId = computed(() => this.currentUser()?.id ?? null);

  /** El estado, solo si es de la cuenta de ahora. */
  private readonly ownState = computed(() => {
    const state = this.state();
    return state.owner !== null && state.owner === this.currentId() ? state : EMPTY_STATE;
  });

  private readonly remainingByChannel = Object.fromEntries(
    CHANNELS.map((channel) => [
      channel,
      computed(() => Math.max(0, Math.ceil((this.ownState().until[channel] - this.now()) / 1000))),
    ]),
  ) as PerChannel<Signal<number>>;

  private readonly busyByChannel = Object.fromEntries(
    CHANNELS.map((channel) => [channel, computed(() => this.ownState().busy[channel])]),
  ) as PerChannel<Signal<boolean>>;

  constructor() {
    // Cambio de cuenta (incluido `null`): fuera la espera de la anterior y su reloj.
    effect(() => {
      const id = this.currentId();
      untracked(() => {
        if (this.state().owner !== null && this.state().owner !== id) this.clear();
      });
    });
    inject(DestroyRef).onDestroy(() => {
      this.cancelRequests();
      this.stopTicker();
    });
  }

  /** Segundos de espera que quedan en `channel` para la cuenta actual (0 = libre). */
  remaining(channel: AccountMailChannel): Signal<number> {
    return this.remainingByChannel[channel];
  }

  /** ¿Hay un envío de `channel` en vuelo para la cuenta actual? */
  busy(channel: AccountMailChannel): Signal<boolean> {
    return this.busyByChannel[channel];
  }

  /** Arranca `seconds` de espera en `channel` para la cuenta actual. Sin sesión, nada. */
  start(channel: AccountMailChannel, seconds: number): void {
    const owner = this.currentId();
    if (owner === null) return;
    const now = Date.now();
    this.now.set(now);
    this.state.update((state) => {
      const base = state.owner === owner ? state : { ...EMPTY_STATE, owner };
      return { ...base, until: { ...base.until, [channel]: now + seconds * 1000 } };
    });
    this.ensureTicker();
  }

  /**
   * Lanza YA `request` (el `GET` del correo) por `channel` y traduce la respuesta a la espera y al aviso, que emite el
   * observable devuelto (una vez). En SSR, con espera o envío en vuelo en ese canal, o sin sesión, no pide nada
   * (completa sin emitir).
   *
   * La petición la sostiene el store, no quien se suscribe: si el botón se destruye a mitad (cambio de pestaña), el
   * correo que salga arranca igual la espera de su canal. Al cambiar de cuenta el store la cancela, y si la respuesta
   * llegara antes de que corra el efecto, con otra cuenta dentro ni arranca espera ni emite.
   */
  send(channel: AccountMailChannel, request: Observable<unknown>): Observable<AccountMailOutcome> {
    // Solo en navegador (§5.6.6, H2 de la Astra p1): en SSR ni se crea ni se suscribe la petición.
    if (!this.isBrowser) return EMPTY;
    const owner = this.currentId();
    if (owner === null || this.remaining(channel)() > 0 || this.busy(channel)()) return EMPTY;

    this.setBusy(owner, channel, true);
    const stillOwner = () => this.currentId() === owner;
    const outcome = new ReplaySubject<AccountMailOutcome>(1);

    this.requests[channel]?.unsubscribe();
    this.requests[channel] = request
      .pipe(
        map(() => ({ ok: true as const })),
        catchError((err: unknown) => of({ ok: false as const, err })),
        filter(() => stillOwner()),
        map((res) => (res.ok ? this.onSent(channel) : this.onFailed(channel, res.err))),
        finalize(() => this.setBusy(owner, channel, false)),
      )
      .subscribe(outcome);

    return outcome.asObservable();
  }

  private onSent(channel: AccountMailChannel): AccountMailOutcome {
    this.start(channel, ACCOUNT_MAIL_WAIT_S);
    return { kind: 'sent' };
  }

  private onFailed(channel: AccountMailChannel, err: unknown): AccountMailOutcome {
    if (!(err instanceof HttpErrorResponse)) return { kind: 'error' };

    if (err.status === 429) {
      const retryAfter = retryAfterSeconds(err.headers.get('Retry-After')) ?? 0;
      this.start(channel, Math.max(ACCOUNT_MAIL_WAIT_S, retryAfter));
      return { kind: 'rate-limited' };
    }

    if (err.status === 422) {
      // Ya verificada en otra pestaña, o el correo de la sesión ya no es el de la cuenta: el texto del servidor
      // (decisión de Adrián del 02/10). No salió correo: sin espera.
      const body = err.error as { error?: unknown } | null;
      const message = typeof body?.error === 'string' && body.error.trim() ? body.error : PROFILE_COPY.SETTINGS.MAIL_ERROR;
      return { kind: 'rejected', message };
    }

    return { kind: 'error' };
  }

  private setBusy(owner: number, channel: AccountMailChannel, busy: boolean): void {
    this.state.update((state) => {
      // Al terminar, solo se libera el estado de esa misma cuenta; al empezar, otra cuenta parte de cero.
      if (state.owner !== owner && !busy) return state;
      const base = state.owner === owner ? state : { ...EMPTY_STATE, owner };
      return { ...base, busy: { ...base.busy, [channel]: busy } };
    });
  }

  private clear(): void {
    this.cancelRequests();
    this.state.set(EMPTY_STATE);
    this.stopTicker();
  }

  private cancelRequests(): void {
    for (const channel of CHANNELS) {
      this.requests[channel]?.unsubscribe();
      this.requests[channel] = null;
    }
  }

  private ensureTicker(): void {
    if (!this.isBrowser || this.timer !== null) return;
    this.timer = setInterval(() => {
      const now = Date.now();
      this.now.set(now);
      const { until } = untracked(() => this.ownState());
      if (CHANNELS.every((channel) => until[channel] <= now)) this.stopTicker();
    }, 1000);
  }

  private stopTicker(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}
